import { useCallback, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import Chart from "chart.js/auto";
import { useAuth } from "context/AuthContext";
import { financeApi } from "services/FinanceService";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

export const money = (value) =>
  value === null || value === undefined
    ? "—"
    : new Intl.NumberFormat("en-NG", {
        style: "currency",
        currency: "NGN",
        minimumFractionDigits: 2,
      }).format(value);
export const dateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleString("en-NG", { timeZone: "Africa/Lagos" });
};
export function localDate(date = new Date()) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Africa/Lagos",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}
export function useAdminData(api, path, params, options = {}) {
  const { enabled = true } = options;
  const { token, logout } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [version, setVersion] = useState(0);
  const key = JSON.stringify(params || {});
  useEffect(() => {
    if (!enabled) {
      setData(null);
      setError(null);
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setData(null);
    api(path, {
      token,
      params: JSON.parse(key),
      signal: controller.signal,
    })
      .then((result) => {
        if (!controller.signal.aborted) setData(result);
      })
      .catch((err) => {
        if (!controller.signal.aborted) {
          setError(err.message);
          if (err.status === 401) logout();
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [api, path, key, token, version, logout, enabled]);
  const refresh = useCallback(() => setVersion((v) => v + 1), []);
  return { data, error, loading, refresh };
}
export function useFinanceData(path, params, options) {
  return useAdminData(financeApi, path, params, options);
}
export function Page({
  title,
  description,
  children,
  actions,
  eyebrow = "Financial operations",
}) {
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <main className="finance-page">
        <div className="page-heading">
          <div>
            <span className="eyebrow">{eyebrow}</span>
            <h1>{title}</h1>
            <p>{description}</p>
          </div>
          <div className="toolbar">{actions}</div>
        </div>
        {children}
      </main>
    </DashboardLayout>
  );
}
export function Panel({ title, children, subtitle }) {
  return (
    <section className="finance-panel">
      {title && (
        <div className="panel-heading">
          <h2>{title}</h2>
          {subtitle && <p>{subtitle}</p>}
        </div>
      )}
      {children}
    </section>
  );
}
export function Badge({ value }) {
  const status = String(value ?? "Unavailable").toUpperCase();
  const tone = [
    "SUCCESS",
    "SUCCESSFUL",
    "ACTIVE",
    "BALANCED",
    "APPROVED",
    "DISBURSED",
  ].includes(status)
    ? "green"
    : ["FAILED", "SUSPENDED", "LOCKED", "UNBALANCED"].includes(status)
    ? "red"
    : ["PENDING", "PENDING_APPROVAL", "PROCESSING", "PENDING_PTB"].includes(
        status
      )
    ? "amber"
    : status === "REJECTED"
    ? "purple"
    : "gray";
  return (
    <span className={`status-badge ${tone}`}>{status.replace(/_/g, " ")}</span>
  );
}
export function LoadState({ loading, error, refresh }) {
  if (loading)
    return (
      <div className="loading-state" role="status">
        <span className="loading-dot" />
        Loading financial data…
      </div>
    );
  if (error)
    return (
      <div className="notice error" role="alert">
        {error}{" "}
        <button className="fin-button secondary" onClick={refresh}>
          Retry
        </button>
      </div>
    );
  return null;
}
export function Unavailable({ title, children }) {
  return (
    <Panel title={title}>
      <div className="unavailable">
        <span className="status-badge gray">Integration unavailable</span>
        <p>{children}</p>
      </div>
    </Panel>
  );
}
export function Stats({ items }) {
  return (
    <div className="stat-grid">
      {items.map(([label, value, note]) => (
        <section className="finance-stat" key={label}>
          <span>{label}</span>
          <strong>{value}</strong>
          {note && <small>{note}</small>}
        </section>
      ))}
    </div>
  );
}
export function DataTable({ columns, rows, rowKey }) {
  return (
    <div className="fin-table-scroll">
      <table className="fin-table">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length}>
                <div className="empty-state">No records found.</div>
              </td>
            </tr>
          ) : (
            rows.map((row, index) => (
              <tr key={row[rowKey] ?? index}>
                {columns.map((c) => (
                  <td key={c.key}>
                    {c.render ? c.render(row) : row[c.key] ?? "—"}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
export function Field({ label, name, value, onChange, options, ...props }) {
  return (
    <label className="fin-field">
      <span>{label}</span>
      {options ? (
        <select name={name} value={value ?? ""} onChange={onChange} {...props}>
          {options.map((option) => (
            <option key={option} value={option}>
              {option || "All / None"}
            </option>
          ))}
        </select>
      ) : (
        <input name={name} value={value ?? ""} onChange={onChange} {...props} />
      )}
    </label>
  );
}
export function Modal({
  title,
  children,
  onClose,
  onSubmit,
  busy,
  disabled,
  action = "Save",
  danger = false,
}) {
  return (
    <Dialog
      open
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="finance-dialog-title"
    >
      <form onSubmit={onSubmit}>
        <DialogTitle id="finance-dialog-title">{title}</DialogTitle>
        <DialogContent>
          <div className="finance-form">{children}</div>
        </DialogContent>
        <DialogActions>
          <button
            type="button"
            className="fin-button secondary"
            disabled={busy}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className={`fin-button ${danger ? "danger" : ""}`}
            type="submit"
            disabled={busy || disabled}
          >
            {busy ? "Submitting…" : action}
          </button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
export function FinancialChart({ title, labels, values, type = "bar" }) {
  const canvas = useRef(null);
  const key = JSON.stringify({ labels, values });
  useEffect(() => {
    const series = JSON.parse(key);
    const chart = new Chart(canvas.current, {
      type,
      data: {
        labels: series.labels,
        datasets: [
          {
            label: title,
            data: series.values,
            backgroundColor: [
              "#16a34a",
              "#3439a5",
              "#f59e0b",
              "#6172f3",
              "#22c55e",
              "#94a3b8",
            ],
            borderRadius: type === "bar" ? 6 : undefined,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: type === "doughnut", position: "bottom" },
          tooltip: { callbacks: { label: (context) => money(context.raw) } },
        },
        ...(type === "bar"
          ? {
              scales: {
                y: { beginAtZero: true, ticks: { callback: (v) => money(v) } },
                x: { grid: { display: false } },
              },
            }
          : {}),
      },
    });
    return () => chart.destroy();
  }, [key, title, type]);
  return (
    <Panel title={title}>
      <div className="financial-chart">
        <canvas
          ref={canvas}
          role="img"
          aria-label={`${title}: ${labels
            .map((label, i) => `${label} ${money(values[i])}`)
            .join(", ")}`}
        />
      </div>
    </Panel>
  );
}
